class UpsellProducts {
  constructor() {
    this.selectedItems = new Map();
    this.currentProductUrl = '';
    this.currentProductTitle = '';
    this.modal = null;
    this.discountCodes = [];
    this.init();
  }

  init() {
    this.bindEvents();
    this.initCollapsible();
    this.initModal();
    this.updateTotal();
    this.hookIntoAddToCart();
  }

  bindEvents() {
    // 監聽複選框變化
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('m-upsell-checkbox')) {
        this.handleCheckboxChange(e.target);
      }
    });

    // 監聽變體選擇變化
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('m-upsell-variant-select')) {
        this.handleVariantChange(e.target);
      }
    });

    // 監聽數量變化
    document.addEventListener('input', (e) => {
      if (e.target.classList.contains('m-upsell-quantity-input')) {
        this.updateTotal();
      }
    });

    // 監聽放大鏡按鈕點擊
    document.addEventListener('click', (e) => {
      if (e.target.closest('.m-upsell-zoom-btn')) {
        e.preventDefault();
        e.stopPropagation();
        const button = e.target.closest('.m-upsell-zoom-btn');
        this.handleZoomClick(button);
      }
    });
  }

  initCollapsible() {
    const triggers = document.querySelectorAll('[data-collapsible-trigger]');
    // 進頁面時預設收合（block 設定 collapse_default：none／mobile／all；mobile 以 860px 為界，跟 seic-product.css 的手機定義一致）
    const mobile = window.matchMedia('(max-width: 860px)').matches;
    triggers.forEach(trigger => {
      const content = trigger.nextElementSibling;
      const button = trigger.querySelector('.m-upsell-toggle-btn');
      const icon = button && button.querySelector('.m-upsell-toggle-icon');
      if (content && content.hasAttribute('data-collapse-default')) {
        const mode = content.getAttribute('data-collapse-default');
        if (mode === 'all' || (mode === 'mobile' && mobile)) {
          content.classList.add('collapsed');
          if (button) button.setAttribute('aria-expanded', 'false');
          if (icon) icon.style.transform = 'rotate(-90deg)';
        }
        content.classList.add('is-init');   // 交回給 .collapsed 控制，CSS 的預先隱藏到此為止
      }
    });
    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const content = trigger.nextElementSibling;
        const button = trigger.querySelector('.m-upsell-toggle-btn');
        const icon = button.querySelector('.m-upsell-toggle-icon');
        
        if (content.classList.contains('collapsed')) {
          content.classList.remove('collapsed');
          button.setAttribute('aria-expanded', 'true');
          icon.style.transform = 'rotate(0deg)';
        } else {
          content.classList.add('collapsed');
          button.setAttribute('aria-expanded', 'false');
          icon.style.transform = 'rotate(-90deg)';
        }
      });
    });
  }

  initModal() {
    this.modal = document.getElementById('upsell-confirm-modal');
    if (!this.modal) return;

    // 獲取元素
    const closeBtn = this.modal.querySelector('.m-upsell-modal__close');
    const cancelBtn = this.modal.querySelector('[data-modal-cancel]');
    const confirmBtn = this.modal.querySelector('[data-modal-confirm]');
    const backdrop = this.modal.querySelector('.m-upsell-modal__backdrop');

    // 綁定關閉事件
    [closeBtn, cancelBtn, backdrop].forEach(element => {
      if (element) {
        element.addEventListener('click', (e) => {
          e.preventDefault();
          this.hideModal();
        });
      }
    });

    // 確認按鈕
    if (confirmBtn) {
      confirmBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.currentProductUrl) {
          window.open(this.currentProductUrl, '_blank', 'noopener,noreferrer');
        }
        this.hideModal();
      });
    }

    // ESC 鍵關閉
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isModalOpen()) {
        this.hideModal();
      }
    });

    // 焦點陷阱
    this.modal.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && this.isModalOpen()) {
        this.handleTabKey(e);
      }
    });
  }

  handleTabKey(e) {
    const focusableElements = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }

  handleZoomClick(button) {
    this.currentProductUrl = button.dataset.productUrl;
    this.currentProductTitle = button.dataset.productTitle;
    this.showModal();
  }

  showModal() {
    if (!this.modal) return;
    
    // 更新商品名稱
    const productNameElement = this.modal.querySelector('[data-product-name]');
    if (productNameElement) {
      productNameElement.textContent = this.currentProductTitle;
    }
    
    // 顯示 modal
    this.modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    
    // 焦點管理
    setTimeout(() => {
      const confirmBtn = this.modal.querySelector('[data-modal-confirm]');
      if (confirmBtn) {
        confirmBtn.focus();
      }
    }, 150);

    // 添加顯示動畫
    requestAnimationFrame(() => {
      this.modal.classList.add('show');
    });
  }

  hideModal() {
    if (!this.modal) return;
    
    this.modal.style.display = 'none';
    document.body.style.overflow = '';
    this.modal.classList.remove('show');
    this.currentProductUrl = '';
    this.currentProductTitle = '';
  }

  isModalOpen() {
    return this.modal && this.modal.style.display !== 'none';
  }

  handleCheckboxChange(checkbox) {
    const productItem = checkbox.closest('.m-upsell-product-item');
    const selectionMode = checkbox.getAttribute('data-selection-mode') || 'multiple';
    
    // ⭐ 單選模式:當選中新項目時,取消其他所有選項
    if (selectionMode === 'single' && checkbox.checked) {
      const allCheckboxes = document.querySelectorAll('.m-upsell-checkbox');
      allCheckboxes.forEach(otherCheckbox => {
        if (otherCheckbox !== checkbox) {
          otherCheckbox.checked = false;
          const otherProductItem = otherCheckbox.closest('.m-upsell-product-item');
          if (otherProductItem) {
            otherProductItem.classList.remove('m-upsell-product-item--selected');
          }
          // 從 selectedItems 中移除
          const otherProductId = otherProductItem?.getAttribute('data-product-id');
          if (otherProductId) {
            this.selectedItems.delete(otherProductId);
          }
        }
      });
    }
    
    if (checkbox.checked) {
      productItem.classList.add('m-upsell-product-item--selected');
      
      const variantId = checkbox.getAttribute('data-variant-id');
      const price = parseFloat(checkbox.getAttribute('data-price'));
      const discountedPrice = parseFloat(checkbox.getAttribute('data-discounted-price'));
      const discountType = checkbox.getAttribute('data-discount-type');
      const discountValue = parseFloat(checkbox.getAttribute('data-discount-value')) || 0;
      const discountCode = checkbox.getAttribute('data-discount-code');
      const customNote = checkbox.getAttribute('data-custom-note');
      const productId = productItem.getAttribute('data-product-id');
      
      const quantityInput = productItem.querySelector('.m-upsell-quantity-input');
      const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
      
      this.selectedItems.set(productId, {
        variantId,
        price,
        discountedPrice,
        quantity,
        discountType,
        discountValue,
        discountCode,
        customNote
      });
      
      // 儲存折扣碼(如果有)
      if (discountCode && discountCode !== '') {
        if (!this.discountCodes.includes(discountCode)) {
          this.discountCodes.push(discountCode);
        }
      }
    } else {
      productItem.classList.remove('m-upsell-product-item--selected');
      const productId = productItem.getAttribute('data-product-id');
      
      // 移除折扣碼
      const checkbox_element = productItem.querySelector('.m-upsell-checkbox');
      const discountCode = checkbox_element?.getAttribute('data-discount-code');
      if (discountCode) {
        const index = this.discountCodes.indexOf(discountCode);
        if (index > -1) {
          this.discountCodes.splice(index, 1);
        }
      }
      
      this.selectedItems.delete(productId);
    }
    
    this.updateTotal();
  }

  handleVariantChange(select) {
    const selectedOption = select.options[select.selectedIndex];
    const productItem = select.closest('.m-upsell-product-item');
    const checkbox = productItem.querySelector('.m-upsell-checkbox');
    const priceElement = productItem.querySelector('[data-current-price]');
    const quantityInput = productItem.querySelector('.m-upsell-quantity-input');
    const productImage = productItem.querySelector('.m-upsell-product-image');

    // 更新複選框的數據屬性
    const originalPrice = parseInt(selectedOption.dataset.price);
    checkbox.setAttribute('data-variant-id', selectedOption.value);
    checkbox.setAttribute('data-price', originalPrice);

    // 計算折扣後價格
    const discountType = checkbox.dataset.discountType;
    const discountValue = parseFloat(checkbox.dataset.discountValue) || 0;
    let discountedPrice = originalPrice;

    if (discountType === 'percentage' && discountValue > 0) {
      const discountAmount = originalPrice * (discountValue / 100);
      discountedPrice = originalPrice - discountAmount;
    } else if (discountType === 'fixed' && discountValue > 0) {
      const discountAmountCents = discountValue * 100;
      discountedPrice = Math.max(0, originalPrice - discountAmountCents);
    }

    checkbox.setAttribute('data-discounted-price', discountedPrice);

    // 更新顯示價格
    if (discountType === 'free') {
      // 免費加購：顯示免費標示，原價劃線
      priceElement.innerHTML = '<span class="m-upsell-free-text">免費</span>';
      const originalPriceElement = productItem.querySelector('[data-original-price]');
      if (originalPriceElement && originalPrice > 0) {
        originalPriceElement.textContent = this.formatMoney(originalPrice);
      }
    } else if (discountType !== 'none' && discountValue > 0) {
      if (discountedPrice === 0) {
        priceElement.innerHTML = '<span class="m-upsell-free-text">免費</span>';
      } else {
        priceElement.textContent = this.formatMoney(discountedPrice);
      }
      // 更新原價顯示
      const originalPriceElement = productItem.querySelector('[data-original-price]');
      if (originalPriceElement) {
        originalPriceElement.textContent = this.formatMoney(originalPrice);
      }
    } else {
      priceElement.textContent = this.formatMoney(originalPrice);
    }

    // 更新商品圖片（如果變體有圖片）
    const variantImage = selectedOption.dataset.image;
    if (variantImage && productImage) {
      productImage.src = variantImage;
    }

    // 更新庫存限制
    const maxQuantity = selectedOption.dataset.inventory || 999;
    if (quantityInput) {
      quantityInput.setAttribute('max', maxQuantity);
      if (parseInt(quantityInput.value) > maxQuantity) {
        quantityInput.value = maxQuantity;
      }
    }

    this.updateSelectedItems();
    this.updateTotal();
  }

  updateSelectedItems() {
    const checkboxes = document.querySelectorAll('.m-upsell-checkbox:checked');
    this.selectedItems.clear();
    
    checkboxes.forEach(checkbox => {
      const productItem = checkbox.closest('.m-upsell-product-item');
      const quantityInput = productItem.querySelector('.m-upsell-quantity-input');
      const productId = checkbox.dataset.productId || productItem.dataset.productId;
      
      const itemData = {
        variantId: checkbox.dataset.variantId,
        quantity: quantityInput ? parseInt(quantityInput.value) || 1 : 1,
        originalPrice: parseInt(checkbox.dataset.price),
        discountedPrice: parseInt(checkbox.dataset.discountedPrice) || parseInt(checkbox.dataset.price),
        discountType: checkbox.dataset.discountType,
        discountValue: checkbox.dataset.discountValue,
        discountCode: checkbox.dataset.discountCode,
        customNote: checkbox.dataset.customNote || '',
        handle: checkbox.dataset.productHandle
      };
      
      this.selectedItems.set(productId, itemData);
    });
  }

  updateTotal() {
    let total = 0;
    const checkboxes = document.querySelectorAll('.m-upsell-checkbox:checked');
    
    checkboxes.forEach(checkbox => {
      const discountedPrice = parseInt(checkbox.dataset.discountedPrice) || parseInt(checkbox.dataset.price);
      const productItem = checkbox.closest('.m-upsell-product-item');
      const quantityInput = productItem.querySelector('.m-upsell-quantity-input');
      const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
      
      total += discountedPrice * quantity;
    });

    // 更新顯示的總金額
    const totalElements = document.querySelectorAll('[data-upsell-total]');
    totalElements.forEach(element => {
      element.textContent = this.formatMoney(total);
    });

    // 觸發自定義事件
    document.dispatchEvent(new CustomEvent('upsell:totalChanged', {
      detail: { total, selectedItems: Array.from(this.selectedItems.values()) }
    }));
  }

  async hookIntoAddToCart() {
    // 攔截所有加入購物車按鈕
    const addToCartButtons = document.querySelectorAll('button[name="add"], .btn-atc, [data-add-to-cart]');
    
    addToCartButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        if (this.selectedItems.size > 0) {
          e.preventDefault();
          await this.addAllItemsToCart(button);
        }
      });
    });
  }

  async addAllItemsToCart(originalButton) {
    let originalText = '';
    
    try {
      // 顯示載入狀態
      originalText = originalButton.textContent;
      originalButton.textContent = '加入中...';
      originalButton.disabled = true;

      const itemsToAdd = [];

      // 1. 添加主商品
      const mainProductForm = document.querySelector('product-form form, form[action*="/cart/add"]');
      if (mainProductForm) {
        const formData = new FormData(mainProductForm);
        const mainItem = {
          id: formData.get('id'),
          quantity: parseInt(formData.get('quantity')) || 1
        };

        const properties = {};
        for (let [key, value] of formData.entries()) {
          if (key.startsWith('properties[') && value.trim() !== '') {
            const propKey = key.replace('properties[', '').replace(']', '');
            properties[propKey] = value;
          }
        }
        
        if (Object.keys(properties).length > 0) {
          mainItem.properties = properties;
        }

        itemsToAdd.push(mainItem);
      }

      // 2. 添加所有選中的加購商品
      this.selectedItems.forEach((item) => {
        const upsellItem = {
          id: item.variantId,
          quantity: item.quantity,
          properties: {
            '類型': '加購商品'
          }
        };

        if (item.customNote && item.customNote.trim() !== '') {
          upsellItem.properties['備註'] = item.customNote.trim();
        }

        if (item.discountType !== 'none' && item.discountValue > 0) {
          if (item.discountType === 'percentage') {
            upsellItem.properties['折扣'] = `${item.discountValue}% 折扣`;
          } else if (item.discountType === 'fixed') {
            upsellItem.properties['折扣'] = `NT$ ${item.discountValue} 折扣`;
          }
        }

        // 將折扣碼存入隱藏 property（底線開頭不顯示），供購物車頁自動套用
        if (item.discountCode && item.discountCode.trim() !== '') {
          upsellItem.properties['_addon_discount_code'] = item.discountCode.trim();
        }

        itemsToAdd.push(upsellItem);
      });


      // 3. 批量添加到購物車
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: itemsToAdd
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '添加到購物車失敗');
      }

      const addedItems = await response.json();

      // 4. 計算返回路徑（帶購物車開啟標記）
      const url = new URL(window.location.href);
      url.searchParams.set('cart', 'open');
      url.searchParams.set('items', itemsToAdd.length);
      // Shopify /discount/CODE?redirect= 只接受相對路徑（不能是完整 https:// URL）
      const returnPath = url.pathname + url.search;

      // 5. 跳回原頁面（折扣碼由 cart-drawer 讀取 _addon_discount_code property 套用）
      window.location.href = returnPath;

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showErrorMessage(error.message || '加入購物車時發生錯誤,請重試。');
      
      // 恢復按鈕狀態
      if (originalButton && originalText) {
        originalButton.textContent = originalText;
        originalButton.disabled = false;
      }
    }
  }

  async applyDiscountCodes() {
    // 應用每個折扣碼
    for (const code of this.discountCodes) {
      if (code && code.trim() !== '') {
        try {
          await fetch('/discount/' + encodeURIComponent(code.trim()), {
            method: 'GET'
          });
        } catch (error) {
          console.warn('Failed to apply discount code:', code, error);
        }
      }
    }
  }

  clearSelections() {
    const checkboxes = document.querySelectorAll('.m-upsell-checkbox:checked');
    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
      const productItem = checkbox.closest('.m-upsell-product-item');
      productItem.classList.remove('selected');
    });
    
    this.selectedItems.clear();
    this.discountCodes = [];
    this.updateTotal();
  }

  showSuccessMessage(message) {
    this.showToast(message, 'success');
  }

  showErrorMessage(message) {
    this.showToast(message, 'error');
  }

  showToast(message, type) {
    // 移除現有的 toast
    const existingToasts = document.querySelectorAll('.upsell-toast');
    existingToasts.forEach(toast => toast.remove());

    // 創建新的 toast
    const toast = document.createElement('div');
    toast.className = `upsell-toast upsell-toast--${type}`;
    toast.textContent = message;
    
    const bgColor = type === 'success' ? '#28a745' : '#dc3545';
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${bgColor};
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 8px;
      z-index: 10001;
      box-shadow: 0 8px 32px rgba(0,0,0,0.2);
      transform: translateX(100%);
      transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
      max-width: 350px;
      word-wrap: break-word;
      font-size: 0.9rem;
      line-height: 1.4;
    `;
    
    document.body.appendChild(toast);
    
    // 顯示動畫
    requestAnimationFrame(() => {
      toast.style.transform = 'translateX(0)';
    });
    
    // 自動移除
    const duration = type === 'success' ? 4000 : 6000;
    setTimeout(() => {
      toast.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 400);
    }, duration);
  }

  formatMoney(cents) {
    return 'NT$ ' + (cents / 100).toLocaleString('zh-TW');
  }
}

// 初始化
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.m-upsell-products')) {
    window.upsellProducts = new UpsellProducts();
  }
});

// 支援購物車更新事件
document.addEventListener('cart:refresh', () => {
  // 如果你的主題有購物車更新函數，在這裡調用
  if (typeof updateCart === 'function') {
    updateCart();
  } else if (typeof refreshCart === 'function') {
    refreshCart();
  }
});
